import { NavigateFunction } from 'react-router-dom';
import { getDataStorageData } from '../../../api/data-storage';
import { routeByStatus, routes } from '../../../routes';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { requestLegitimationResultValidation, requestLegitimationStart } from '../../../api/legitimation';
import { removeFile, submitFile, createPresignedUrl, uploadDocumentToUrl } from '../../../api/files';
import { UploadFileBody } from '../../../models/upload.model';
import { trackingService } from '../../../tracking/tracking.service';
import { readDocument } from '../../../pages/upload-page/utils';

export const fetchDataStorageData = createAsyncThunk(
  'appData/fetchDataStorageData',
  async ({ storefrontId, token, navigate }: { storefrontId: string; token: string; navigate: NavigateFunction }) => {
    const dataStorageObject = await getDataStorageData({
      transactionId: storefrontId,
      token
    });
    trackingService.extendWithInitialTransactionData({
      core: {
        attributes: {
          transactionID: dataStorageObject.data.transaction.id
        }
      }
    });
    navigate(
      routeByStatus(dataStorageObject.data.transaction.status, {
        requestInfoType: dataStorageObject?.data?.custom?.requestInfoType
      })
    );
    return dataStorageObject;
  }
);

export const startLegitimation = createAsyncThunk(
  'appData/startLegitimation',
  async ({
    storefrontId,
    token,
    isOnfidoMotionEnabled
  }: {
    storefrontId: string;
    token: string;
    isOnfidoMotionEnabled: boolean;
  }) => {
    return requestLegitimationStart({
      transactionId: storefrontId,
      token,
      isOnfidoMotionEnabled
    });
  }
);

export const validateLegitimationResult = createAsyncThunk(
  'appData/validateLegitimationResult',
  async ({ storefrontId, token }: { storefrontId: string; token: string; navigate: NavigateFunction }) => {
    const dsObject = requestLegitimationResultValidation({
      transactionId: storefrontId,
      token
    });
    return dsObject;
  }
);

export const uploadFiles = createAsyncThunk(
  'appData/uploadFiles',
  async ({ storefrontId, token, files }: { storefrontId: string; token: string; files: UploadFileBody[] }) => {
    const fileIds: string[] = [];
    for (const file of files) {
      if (!file.size) {
        throw new Error('File cannot be empty');
      }

      try {
        // Step 1: Get presigned URL
        const { fileId, presignedUrl } = await createPresignedUrl({
          transactionId: storefrontId,
          token,
          files: [file]
        });
        fileIds.push(fileId);

        // Step 2: Convert file to binary content
        const binaryContent = await readDocument(file.file as File);

        // Step 3: Upload the file using the presigned URL
        await uploadDocumentToUrl(binaryContent, presignedUrl);
      } catch (error) {
        console.error(`Error uploading file ${file.filename}:`, error);
        throw error;
      }
    }
    return fileIds;
  }
);

export const deleteFile = createAsyncThunk(
  'appData/deleteFile',
  async ({ storefrontId, token, fileId }: { storefrontId: string; token: string; fileId: string }) => {
    return removeFile({
      transactionId: storefrontId,
      token,
      fileId
    });
  }
);

export const setDocumentsSubmitted = createAsyncThunk(
  'appData/setDocumentsSubmitted',
  async ({ storefrontId, token, navigate }: { storefrontId: string; token: string; navigate: NavigateFunction }) => {
    const dataStorageObject = await submitFile({
      transactionId: storefrontId,
      token
    });
    // The user is not supposed to wait for the right status being asynchronously set by integration service,
    // if we get "success" from FES we assume we can show success page
    navigate(routes.application.legitimationResult);
    return dataStorageObject;
  }
);
